import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  t8_subscribers_activate_tomi_subscription,
  t8_subscribers_add_payment,
  t8_subscribers_create,
  t8_subscribers_get_by_serial,
  t8_subscribers_link_tomi_email,
  t8_subscribers_list_pin_types,
  t8_subscribers_list_plans,
  t8_subscribers_summary,
  t8_subscribers_sync_tomi_subscription_ends,
  t8_subscribers_tomi_subscription_status,
  t8_subscribers_unlink_tomi_email,
  t8_subscribers_update,
  t8_subscribers_update_tomi_subscription_type,
} from '../../../utils/services';
import {
  T8_MSUBSCRIPTIONS_QA_STORAGE_KEY,
  formatTomiEndsDisplay,
  normalizeDateYmd,
  t8MsubscriptionsIsDevSwitch,
  t8MsubscriptionsReadQaFlag,
  tomiDisplayName,
  tomiHasActiveInternal,
  tomiInternalEndsAt,
  tomiSubscriptionEndsAt,
} from '../../../utils/tomi_subscription_display';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './SuscripcionesT8.css';

/** Solo en npm start (NODE_ENV=development). El build de producción siempre usa PROD. */
const T8_MSUBSCRIPTIONS_DEV_SWITCH = t8MsubscriptionsIsDevSwitch();

function readStoredQa() {
  return t8MsubscriptionsReadQaFlag();
}

function formatPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value || '0.00';
  return `$${num.toFixed(2)}`;
}

function statusBadge(status) {
  if (status === 'activo') return <span className="badge bg-success">Activo</span>;
  if (status === 'mora') return <span className="badge bg-warning text-dark">En mora</span>;
  if (status === 'suspendido') return <span className="badge bg-danger">Suspendido</span>;
  return <span className="badge bg-secondary">{status || '—'}</span>;
}

function cycleLabel(cycle) {
  return cycle === 'annual' ? 'Anual' : 'Mensual';
}

function getPinTypeLabel(item) {
  if (!item) return '';
  return item.name || item.label || item.title || item.description || String(item.id ?? '');
}

function EnvToggle({ isQa, onChange }) {
  return (
    <div className="suscripciones-t8-env-toggle d-inline-flex align-items-center gap-2">
      <span className={`badge ${isQa ? 'bg-warning text-dark' : 'bg-success'}`}>
        {isQa ? 'QA' : 'PROD'}
      </span>
      <Form.Check
        type="switch"
        id="t8-msubscriptions-env"
        label={isQa ? 'Usar PROD' : 'Usar QA'}
        checked={isQa}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}

function SuscripcionesT8({ permission, token }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionToken = token || localStorage.getItem('token');
  const canWrite = Number(permission) === 1;

  const [serialInput, setSerialInput] = useState('');
  const [summary, setSummary] = useState({ activo: 0, mora: 0, suspendido: 0, by_plan: [] });
  const [plans, setPlans] = useState([]);
  const [subscriber, setSubscriber] = useState(null);
  const [lookupSerial, setLookupSerial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [errorMessage, setErrorMessage] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSerial, setCreateSerial] = useState('');
  const [createPlanId, setCreatePlanId] = useState('');
  const [createCycle, setCreateCycle] = useState('monthly');
  const [createInvoice, setCreateInvoice] = useState('');
  const [createAmount, setCreateAmount] = useState('');
  const [createPaidAt, setCreatePaidAt] = useState('');
  const [savingCreate, setSavingCreate] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payInvoice, setPayInvoice] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payPaidAt, setPayPaidAt] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  const [savingUpdate, setSavingUpdate] = useState(false);

  const [isQaState, setIsQaState] = useState(readStoredQa);
  const isQa = T8_MSUBSCRIPTIONS_DEV_SWITCH ? isQaState : false;
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [tomiUserName, setTomiUserName] = useState('');
  const [tomiStatus, setTomiStatus] = useState(null);
  const [tomiStatusLoading, setTomiStatusLoading] = useState(false);
  const [tomiStatusError, setTomiStatusError] = useState('');
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [changeEmailInput, setChangeEmailInput] = useState('');
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [pinTypes, setPinTypes] = useState([]);
  const [pinTypesLoading, setPinTypesLoading] = useState(false);
  const [selectedPinTypeId, setSelectedPinTypeId] = useState('');
  const [activateReason, setActivateReason] = useState('');
  const [savingActivate, setSavingActivate] = useState(false);
  const [savingSync, setSavingSync] = useState(false);

  const showToast = useCallback((message, variant = 'success') => {
    setToastVariant(variant);
    setToastMessage(message);
  }, []);

  const handleQaChange = useCallback((nextQa) => {
    if (!T8_MSUBSCRIPTIONS_DEV_SWITCH) return;
    setIsQaState(nextQa);
    try {
      localStorage.setItem(T8_MSUBSCRIPTIONS_QA_STORAGE_KEY, nextQa ? 'qa' : 'prod');
    } catch {
      // ignore storage errors
    }
  }, []);

  const fetchTomiStatus = useCallback(async (email, qaFlag) => {
    const trimmed = String(email || '').trim();
    if (!trimmed) {
      setTomiStatus(null);
      setTomiStatusError('');
      setTomiStatusLoading(false);
      return;
    }
    setTomiStatusLoading(true);
    setTomiStatusError('');
    try {
      const res = await t8_subscribers_tomi_subscription_status(sessionToken, {
        email: trimmed,
        qa: qaFlag,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error || res?.ok === false) {
        setTomiStatus(null);
        setTomiStatusError(res.message || 'No se pudo consultar el estado en msubscriptions');
        return;
      }
      setTomiStatus(res.status ?? null);
      setTomiStatusError('');
      const name = tomiDisplayName(res.status);
      if (name) setTomiUserName(name);
    } catch (err) {
      setTomiStatus(null);
      setTomiStatusError('Error al consultar el estado de suscripción TOMi');
    } finally {
      setTomiStatusLoading(false);
    }
  }, [sessionToken, navigate]);

  const loadPinTypes = useCallback(async (qaFlag) => {
    setPinTypesLoading(true);
    try {
      const res = await t8_subscribers_list_pin_types(sessionToken, qaFlag);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      const list = Array.isArray(res?.pinTypes) ? res.pinTypes : [];
      setPinTypes(list);
      setSelectedPinTypeId((prev) => {
        if (prev && list.some((p) => String(p.id) === String(prev))) return prev;
        return list[0]?.id != null ? String(list[0].id) : '';
      });
    } catch (err) {
      setPinTypes([]);
    } finally {
      setPinTypesLoading(false);
    }
  }, [sessionToken, navigate]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const t = setTimeout(() => setToastMessage(''), 5000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const refreshSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await t8_subscribers_summary(sessionToken);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res && !res.error) {
        setSummary({
          activo: res.activo || 0,
          mora: res.mora || 0,
          suspendido: res.suspendido || 0,
          by_plan: Array.isArray(res.by_plan) ? res.by_plan : [],
        });
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [sessionToken, navigate]);

  const loadPlans = useCallback(async () => {
    const res = await t8_subscribers_list_plans(sessionToken);
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    setPlans(Array.isArray(res) ? res : []);
  }, [sessionToken, navigate]);

  const lookupSerialFn = useCallback(async (serial, options = {}) => {
    const trimmed = String(serial || '').trim();
    if (!trimmed) return;

    setLoading(true);
    setErrorMessage('');
    setLookupSerial(trimmed);
    setSerialInput(trimmed);
    setTomiStatus(null);
    setTomiStatusError('');
    setTomiUserName('');
    setLinkEmailInput('');

    try {
      const res = await t8_subscribers_get_by_serial(sessionToken, trimmed);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.code === 'NOT_FOUND' || (res?.error && res?.code === 'NOT_FOUND')) {
        setSubscriber(null);
        setErrorMessage('Serial no encontrado en el inventario.');
        return;
      }
      if (res?.error) {
        setSubscriber(null);
        setErrorMessage(res.message || 'Error al buscar el serial');
        return;
      }

      setSubscriber(res.subscriber || null);
      if (!res.subscriber) {
        setErrorMessage('');
        if (options.openCreateIfMissing && canWrite) {
          setCreateSerial(trimmed);
          setCreatePlanId(plans[0]?.id ? String(plans[0].id) : '');
          setCreateCycle('monthly');
          setCreateInvoice('');
          setCreateAmount('');
          setCreatePaidAt(new Date().toISOString().slice(0, 10));
          setShowCreateModal(true);
        }
      }
    } catch (err) {
      setErrorMessage('Error al buscar el serial');
      setSubscriber(null);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, navigate, canWrite, plans]);

  useEffect(() => {
    if (!subscriber?.tomi_email) {
      setTomiStatus(null);
      setTomiStatusError('');
      return undefined;
    }
    fetchTomiStatus(subscriber.tomi_email, isQa);
    loadPinTypes(isQa);
    return undefined;
  }, [subscriber?.id, subscriber?.tomi_email, isQa, fetchTomiStatus, loadPinTypes]);

  useEffect(() => {
    const typeId = tomiStatus?.internal?.plan_type_id ?? tomiStatus?.effective?.plan_type_id;
    if (typeId == null || pinTypes.length === 0) return;
    if (pinTypes.some((p) => String(p.id) === String(typeId))) {
      setSelectedPinTypeId(String(typeId));
    }
  }, [tomiStatus?.internal?.plan_type_id, tomiStatus?.effective?.plan_type_id, pinTypes]);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    refreshSummary();
    loadPlans();
  }, [sessionToken, navigate, refreshSummary, loadPlans]);

  useEffect(() => {
    const serialFromUrl = searchParams.get('serial');
    if (!serialFromUrl) return;

    const openCreate = searchParams.get('create') === '1';
    lookupSerialFn(serialFromUrl, { openCreateIfMissing: openCreate });

    if (openCreate) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('serial'), searchParams.get('create')]);

  const handleSearch = () => {
    const trimmed = serialInput.trim();
    if (!trimmed) return;
    setSearchParams({ serial: trimmed });
    lookupSerialFn(trimmed);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openCreateModal = (prefillSerial = '') => {
    setCreateSerial(prefillSerial || serialInput.trim() || '');
    setCreatePlanId(plans[0]?.id ? String(plans[0].id) : '');
    setCreateCycle('monthly');
    setCreateInvoice('');
    setCreateAmount('');
    setCreatePaidAt(new Date().toISOString().slice(0, 10));
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (savingCreate || !createSerial.trim() || !createPlanId) return;
    setSavingCreate(true);
    try {
      const payload = {
        serial: createSerial.trim(),
        plan_id: createPlanId,
        billing_cycle: createCycle,
      };
      if (createInvoice.trim()) {
        payload.invoice_number = createInvoice.trim();
        payload.amount = createAmount;
        payload.paid_at = createPaidAt || undefined;
      }
      const res = await t8_subscribers_create(sessionToken, payload);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo crear la suscripción', 'danger');
        return;
      }
      showToast('Suscripción creada');
      setShowCreateModal(false);
      setSubscriber(res.subscriber);
      setLookupSerial(res.subscriber?.serial_num || createSerial.trim());
      setSearchParams({ serial: res.subscriber?.serial_num || createSerial.trim() });
      await refreshSummary();
    } catch (err) {
      showToast('Error al crear la suscripción', 'danger');
    } finally {
      setSavingCreate(false);
    }
  };

  const handleChangePlan = async (planId) => {
    if (!canWrite || !subscriber || savingUpdate) return;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        plan_id: planId,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el plan', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast('Plan actualizado');
      await refreshSummary();
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleChangeCycle = async (cycle) => {
    if (!canWrite || !subscriber || savingUpdate) return;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        billing_cycle: cycle,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el ciclo', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast('Ciclo actualizado');
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleToggleSuspended = async () => {
    if (!canWrite || !subscriber || savingUpdate) return;
    const next = subscriber.suspended ? 0 : 1;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        suspended: next,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el estado', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast(next ? 'Cuenta suspendida' : 'Suspensión removida');
      await refreshSummary();
    } finally {
      setSavingUpdate(false);
    }
  };

  const openPaymentModal = () => {
    setPayInvoice('');
    setPayAmount('');
    setPayPaidAt(new Date().toISOString().slice(0, 10));
    setPayNotes('');
    setShowPaymentModal(true);
  };

  const handleAddPayment = async () => {
    if (savingPayment || !subscriber || !payInvoice.trim() || payAmount === '') return;
    setSavingPayment(true);
    try {
      const res = await t8_subscribers_add_payment(sessionToken, {
        subscriber_id: subscriber.id,
        invoice_number: payInvoice.trim(),
        amount: payAmount,
        paid_at: payPaidAt || undefined,
        notes: payNotes.trim() || undefined,
        qa: isQa,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo registrar el pago', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      setShowPaymentModal(false);
      const sync = res.tomi_sync;
      if (sync && sync.ok === false) {
        showToast(
          `Pago registrado. No se sincronizó TOMi.digital: ${sync.message || 'error'}`,
          'warning'
        );
      } else if (sync && sync.ok) {
        setTomiStatus(sync.status ?? null);
        showToast('Pago registrado y fecha sincronizada en TOMi.digital');
      } else {
        showToast('Pago registrado');
      }
      if (res.subscriber?.tomi_email) {
        await fetchTomiStatus(res.subscriber.tomi_email, isQa);
      }
      await refreshSummary();
    } catch (err) {
      showToast('Error al registrar el pago', 'danger');
    } finally {
      setSavingPayment(false);
    }
  };

  const clearLookup = () => {
    setSubscriber(null);
    setLookupSerial(null);
    setErrorMessage('');
    setSerialInput('');
    setTomiStatus(null);
    setTomiStatusError('');
    setTomiUserName('');
    setLinkEmailInput('');
    setSearchParams({});
  };

  const handleLinkTomiEmail = async (rawEmail) => {
    if (!canWrite || !subscriber || savingLink) return;
    const email = String(rawEmail || '').trim();
    if (!email) {
      showToast('Ingresa un correo electrónico', 'danger');
      return;
    }
    const previous = String(subscriber.tomi_email || '').trim().toLowerCase();
    const next = email.toLowerCase();
    if (previous && previous !== next) {
      const controllable = Boolean(
        tomiStatus?.manageable && tomiHasActiveInternal(tomiStatus)
      );
      const warn = controllable
        ? `Vas a cambiar el correo ${previous} por ${next}.\n\n`
          + 'La suscripción TOMi.digital del correo anterior (que controlamos) se vencerá a ayer y quedará inactiva.\n\n'
          + '¿Continuar?'
        : `Vas a cambiar el correo ${previous} por ${next}.\n\n¿Continuar?`;
      if (!window.confirm(warn)) return;
    }
    setSavingLink(true);
    try {
      const res = await t8_subscribers_link_tomi_email(sessionToken, {
        id: subscriber.id,
        email,
        qa: isQa,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo vincular el correo', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      setTomiUserName(res.tomi_user?.user_name || '');
      setLinkEmailInput('');
      setShowChangeEmailModal(false);
      setChangeEmailInput('');
      const expired = res.tomi_expire && res.tomi_expire.skipped === false;
      showToast(
        expired
          ? 'Correo cambiado. Suscripción del correo anterior inactivada en TOMi.digital.'
          : 'Correo TOMi.digital vinculado'
      );
    } catch (err) {
      showToast('Error al vincular el correo', 'danger');
    } finally {
      setSavingLink(false);
    }
  };

  const handleUnlinkTomiEmail = async () => {
    if (!canWrite || !subscriber || savingLink) return;
    const controllable = Boolean(
      tomiStatus?.manageable && tomiHasActiveInternal(tomiStatus)
    );
    const warn = controllable
      ? `Vas a desvincular ${subscriber.tomi_email}.\n\n`
        + 'La suscripción TOMi.digital de esta cuenta (que controlamos) se vencerá a ayer y quedará inactiva.\n\n'
        + '¿Continuar?'
      : `¿Desvincular el correo ${subscriber.tomi_email || 'TOMi.digital'} de este suscriptor?`;
    if (!window.confirm(warn)) return;
    setSavingLink(true);
    try {
      const res = await t8_subscribers_unlink_tomi_email(sessionToken, subscriber.id, isQa);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo desvincular el correo', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      setTomiUserName('');
      setTomiStatus(null);
      setTomiStatusError('');
      const expired = res.tomi_expire && res.tomi_expire.skipped === false;
      showToast(
        expired
          ? 'Correo desvinculado. Suscripción TOMi.digital inactivada (vence ayer).'
          : 'Correo desvinculado'
      );
    } catch (err) {
      showToast('Error al desvincular el correo', 'danger');
    } finally {
      setSavingLink(false);
    }
  };

  const openChangeEmailModal = () => {
    setChangeEmailInput(subscriber?.tomi_email || '');
    setShowChangeEmailModal(true);
  };

  const handleActivateTomiSubscription = async () => {
    if (!canWrite || !subscriber || savingActivate || !selectedPinTypeId) return;
    setSavingActivate(true);
    try {
      const res = await t8_subscribers_activate_tomi_subscription(sessionToken, {
        id: subscriber.id,
        pin_type_id: selectedPinTypeId,
        reason: activateReason.trim() || undefined,
        qa: isQa,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error || res?.ok === false) {
        showToast(res.message || 'No se pudo activar la suscripción TOMi', 'danger');
        if (res.status) setTomiStatus(res.status);
        return;
      }
      if (res.subscriber) setSubscriber(res.subscriber);
      setTomiStatus(res.status ?? null);
      setActivateReason('');
      showToast('Suscripción TOMi.digital activada');
    } catch (err) {
      showToast('Error al activar la suscripción TOMi', 'danger');
    } finally {
      setSavingActivate(false);
    }
  };

  const handleSyncTomiEnds = async () => {
    if (!canWrite || !subscriber || savingSync) return;
    setSavingSync(true);
    try {
      const res = await t8_subscribers_sync_tomi_subscription_ends(sessionToken, {
        id: subscriber.id,
        qa: isQa,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error || res?.ok === false) {
        showToast(res.message || 'No se pudo sincronizar la fecha', 'danger');
        if (res.status) setTomiStatus(res.status);
        return;
      }
      if (res.subscriber) setSubscriber(res.subscriber);
      setTomiStatus(res.status ?? null);
      showToast('Fechas sincronizadas (T8 → TOMi.digital)');
    } catch (err) {
      showToast('Error al sincronizar fechas', 'danger');
    } finally {
      setSavingSync(false);
    }
  };

  const handleChangeTomiType = async () => {
    if (!canWrite || !subscriber || savingActivate || !selectedPinTypeId) return;
    const currentTypeId = tomiStatus?.internal?.plan_type_id ?? tomiStatus?.effective?.plan_type_id;
    if (currentTypeId != null && String(currentTypeId) === String(selectedPinTypeId)) {
      showToast('Selecciona un tipo distinto al actual', 'warning');
      return;
    }
    setSavingActivate(true);
    try {
      const res = await t8_subscribers_update_tomi_subscription_type(sessionToken, {
        id: subscriber.id,
        pin_type_id: selectedPinTypeId,
        reason: activateReason.trim() || undefined,
        qa: isQa,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error || res?.ok === false) {
        showToast(res.message || 'No se pudo cambiar el tipo de suscripción', 'danger');
        if (res.status) setTomiStatus(res.status);
        return;
      }
      if (res.subscriber) setSubscriber(res.subscriber);
      setTomiStatus(res.status ?? null);
      setActivateReason('');
      setShowChangeTypeModal(false);
      showToast('Tipo de suscripción TOMi.digital actualizado');
    } catch (err) {
      showToast('Error al cambiar el tipo de suscripción', 'danger');
    } finally {
      setSavingActivate(false);
    }
  };

  const openChangeTypeModal = () => {
    const typeId = tomiStatus?.internal?.plan_type_id ?? tomiStatus?.effective?.plan_type_id;
    if (typeId != null) setSelectedPinTypeId(String(typeId));
    setActivateReason('');
    setShowChangeTypeModal(true);
    if (pinTypes.length === 0) loadPinTypes(isQa);
  };

  const showLanding = !lookupSerial;

  const t8DueYmd = normalizeDateYmd(subscriber?.next_due_date);
  const tomiEndsRaw = tomiSubscriptionEndsAt(tomiStatus);
  const tomiInternalEndsYmd = normalizeDateYmd(tomiInternalEndsAt(tomiStatus));
  const datesOutOfSync = Boolean(
    subscriber?.tomi_email
    && t8DueYmd
    && tomiInternalEndsYmd
    && t8DueYmd !== tomiInternalEndsYmd
    && tomiStatus?.manageable
    && tomiHasActiveInternal(tomiStatus)
  );
  const canActivateTomi = Boolean(
    canWrite
    && subscriber?.tomi_email
    && !tomiStatusLoading
    && !tomiStatusError
    && tomiStatus
    && tomiStatus.manageable
    && !tomiHasActiveInternal(tomiStatus)
  );
  const canChangeTomiType = Boolean(
    canWrite
    && subscriber?.tomi_email
    && !tomiStatusLoading
    && !tomiStatusError
    && tomiStatus
    && tomiStatus.manageable
    && tomiHasActiveInternal(tomiStatus)
  );
  const currentTomiTypeId = tomiStatus?.internal?.plan_type_id ?? tomiStatus?.effective?.plan_type_id ?? null;

  return (
    <div className="suscripciones-t8-page">
      {toastMessage && (
        <div className={`alert alert-${toastVariant} suscripciones-t8-toast`} role="alert">
          {toastMessage}
        </div>
      )}

      {showLanding ? (
        <div className="d-flex justify-content-center align-items-start pt-4" style={{ minHeight: '80vh' }}>
          <div className="text-center" style={{ width: '100%', maxWidth: '720px' }}>
            <h1 className="suscripciones-t8-title">Suscripciones de T8</h1>
            <p className="suscripciones-t8-subtitle">Busca por serial del dispositivo</p>
            {T8_MSUBSCRIPTIONS_DEV_SWITCH && (
              <div className="d-flex justify-content-center mt-2">
                <EnvToggle isQa={isQa} onChange={handleQaChange} />
              </div>
            )}

            <div className="mt-4 px-3">
              <input
                type="text"
                className="form-control form-control-lg suscripciones-t8-search"
                placeholder="Escribe el serial..."
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                <button
                  className="btn btn-lg px-5 text-white suscripciones-t8-btn"
                  type="button"
                  onClick={handleSearch}
                  disabled={!serialInput.trim() || loading}
                >
                  Buscar
                </button>
                {canWrite && (
                  <button
                    className="btn btn-lg btn-outline-primary px-4"
                    type="button"
                    onClick={() => openCreateModal()}
                  >
                    Nueva suscripción
                  </button>
                )}
              </div>
            </div>

            <div className="suscripciones-t8-summary mt-5">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-activo">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.activo}</div>
                    <div className="suscripciones-t8-card-label">Activos</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-mora">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.mora}</div>
                    <div className="suscripciones-t8-card-label">En mora</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-suspendido">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.suspendido}</div>
                    <div className="suscripciones-t8-card-label">Suspendidos</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-start">
                <h5 className="mb-3">Suscritos por plan</h5>
                {summary.by_plan.length === 0 ? (
                  <p className="text-muted mb-0">No hay planes registrados.</p>
                ) : (
                  <Table bordered hover size="sm" className="bg-white">
                    <thead className="table-light">
                      <tr>
                        <th>Plan</th>
                        <th style={{ width: '120px' }} className="text-center">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_plan.map((row) => (
                        <tr key={row.plan_id}>
                          <td>{row.plan_name}</td>
                          <td className="text-center">{row.subscriber_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="container-fluid py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <button type="button" className="btn btn-link ps-0" onClick={clearLookup}>
                ← Volver al resumen
              </button>
              <h2 className="h4 mb-0">Serial: <span className="font-monospace">{lookupSerial}</span></h2>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {T8_MSUBSCRIPTIONS_DEV_SWITCH && (
                <EnvToggle isQa={isQa} onChange={handleQaChange} />
              )}
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: '220px' }}
                placeholder="Otro serial..."
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button className="btn btn-primary" type="button" onClick={handleSearch} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>

          {loading && <p className="text-muted">Buscando…</p>}

          {!loading && errorMessage && (
            <div className="alert alert-warning">
              {errorMessage}
            </div>
          )}

          {!loading && !subscriber && !errorMessage && (
            <div className="alert alert-info">
              Este dispositivo no tiene suscripción registrada.
              {canWrite && (
                <div className="mt-2">
                  <Button size="sm" variant="primary" onClick={() => openCreateModal(lookupSerial)}>
                    Crear suscripción
                  </Button>
                </div>
              )}
            </div>
          )}

          {!loading && subscriber && (
            <div className="row g-3">
              <div className="col-lg-5">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="card-title mb-0">Detalle</h5>
                      {statusBadge(subscriber.status)}
                    </div>

                    <dl className="row mb-0 suscripciones-t8-dl">
                      <dt className="col-sm-5">Serial</dt>
                      <dd className="col-sm-7 font-monospace">{subscriber.serial_num}</dd>

                      <dt className="col-sm-5">Plan</dt>
                      <dd className="col-sm-7">
                        {canWrite ? (
                          <Form.Select
                            size="sm"
                            value={String(subscriber.plan_id)}
                            disabled={savingUpdate}
                            onChange={(e) => handleChangePlan(e.target.value)}
                          >
                            {plans.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </Form.Select>
                        ) : (
                          subscriber.plan_name || '—'
                        )}
                      </dd>

                      <dt className="col-sm-5">Ciclo</dt>
                      <dd className="col-sm-7">
                        {canWrite ? (
                          <Form.Select
                            size="sm"
                            value={subscriber.billing_cycle}
                            disabled={savingUpdate}
                            onChange={(e) => handleChangeCycle(e.target.value)}
                          >
                            <option value="monthly">Mensual</option>
                            <option value="annual">Anual</option>
                          </Form.Select>
                        ) : (
                          cycleLabel(subscriber.billing_cycle)
                        )}
                      </dd>

                      <dt className="col-sm-5">Próximo vencimiento</dt>
                      <dd className="col-sm-7">{subscriber.next_due_date || '—'}</dd>

                      <dt className="col-sm-5">Precio ref.</dt>
                      <dd className="col-sm-7">
                        {subscriber.billing_cycle === 'annual'
                          ? formatPrice(subscriber.price_annual)
                          : formatPrice(subscriber.price_monthly)}
                      </dd>
                    </dl>

                    {canWrite && (
                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button
                          size="sm"
                          variant={subscriber.suspended ? 'outline-success' : 'outline-danger'}
                          disabled={savingUpdate}
                          onClick={handleToggleSuspended}
                        >
                          {subscriber.suspended ? 'Reactivar' : 'Suspender'}
                        </Button>
                        <Button size="sm" variant="primary" onClick={openPaymentModal}>
                          Registrar pago
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card shadow-sm mt-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                      <h5 className="card-title mb-0">Cuenta TOMi.digital</h5>
                      {T8_MSUBSCRIPTIONS_DEV_SWITCH && (
                        <span className={`badge ${isQa ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                          API {isQa ? 'QA' : 'PROD'}
                        </span>
                      )}
                    </div>

                    {!subscriber.tomi_email ? (
                      canWrite ? (
                        <div>
                          <p className="text-muted small mb-2">
                            Vincula un correo con cuenta activa en TOMi.digital. Solo un mail por suscriptor.
                          </p>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">Correo electrónico</Form.Label>
                            <Form.Control
                              type="email"
                              size="sm"
                              placeholder="usuario@ejemplo.com"
                              value={linkEmailInput}
                              onChange={(e) => setLinkEmailInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleLinkTomiEmail(linkEmailInput);
                                }
                              }}
                              disabled={savingLink}
                            />
                          </Form.Group>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={savingLink || !linkEmailInput.trim()}
                            onClick={() => handleLinkTomiEmail(linkEmailInput)}
                          >
                            {savingLink ? 'Vinculando…' : 'Vincular'}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">Sin correo vinculado.</p>
                      )
                    ) : (
                      <div>
                        <dl className="row mb-2 suscripciones-t8-dl">
                          <dt className="col-sm-5">Correo</dt>
                          <dd className="col-sm-7">{subscriber.tomi_email}</dd>
                          {(tomiUserName || tomiDisplayName(tomiStatus)) ? (
                            <>
                              <dt className="col-sm-5">Nombre</dt>
                              <dd className="col-sm-7">{tomiUserName || tomiDisplayName(tomiStatus)}</dd>
                            </>
                          ) : null}
                          {subscriber.tomi_user_id ? (
                            <>
                              <dt className="col-sm-5">User ID</dt>
                              <dd className="col-sm-7 font-monospace">{subscriber.tomi_user_id}</dd>
                            </>
                          ) : null}
                        </dl>

                        {tomiStatusLoading && <p className="text-muted small">Consultando msubscriptions…</p>}
                        {!tomiStatusLoading && tomiStatusError && (
                          <div className="alert alert-danger py-2 small mb-2">{tomiStatusError}</div>
                        )}
                        {!tomiStatusLoading && !tomiStatusError && tomiStatus && (
                          <div className="suscripciones-t8-tomi-summary mb-2">
                            <dl className="row mb-0 suscripciones-t8-dl">
                              <dt className="col-sm-5">Acceso efectivo</dt>
                              <dd className="col-sm-7">
                                {tomiStatus.effective?.enabled ? (
                                  <span className="badge bg-success me-1">Activo</span>
                                ) : (
                                  <span className="badge bg-secondary me-1">Inactivo</span>
                                )}
                                <span className="small">
                                  {tomiStatus.effective?.source || 'NONE'}
                                  {tomiStatus.effective?.plan_type_name
                                    ? ` · ${tomiStatus.effective.plan_type_name}`
                                    : ''}
                                </span>
                              </dd>
                              <dt className="col-sm-5">Administrable</dt>
                              <dd className="col-sm-7">
                                {tomiStatus.manageable ? (
                                  <span className="badge bg-success">Sí</span>
                                ) : (
                                  <span className="badge bg-warning text-dark">No</span>
                                )}
                              </dd>
                              {!tomiStatus.manageable && Array.isArray(tomiStatus.blocked_by) && tomiStatus.blocked_by.length > 0 && (
                                <>
                                  <dt className="col-sm-5">Bloqueos</dt>
                                  <dd className="col-sm-7 small">{tomiStatus.blocked_by.join(', ')}</dd>
                                </>
                              )}
                              <dt className="col-sm-5">Interna</dt>
                              <dd className="col-sm-7 small">
                                {tomiStatus.internal?.present ? (
                                  <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                                    <span>
                                      {tomiStatus.internal.active ? 'Activa' : 'Inactiva'}
                                      {tomiStatus.internal.plan_type_name
                                        ? ` · ${tomiStatus.internal.plan_type_name}`
                                        : ''}
                                    </span>
                                    {canChangeTomiType && (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 suscripciones-t8-edit-type"
                                        title="Cambiar tipo de suscripción"
                                        aria-label="Cambiar tipo de suscripción"
                                        onClick={openChangeTypeModal}
                                      >
                                        <i className="bi bi-pencil" aria-hidden="true" />
                                      </button>
                                    )}
                                  </span>
                                ) : (
                                  'Sin suscripción interna'
                                )}
                              </dd>
                              <dt className="col-sm-5">Vence TOMi</dt>
                              <dd className="col-sm-7">
                                {formatTomiEndsDisplay(tomiEndsRaw)}
                                {datesOutOfSync && (
                                  <span className="badge bg-warning text-dark ms-2">Desincronizada vs T8</span>
                                )}
                                {!tomiStatus.manageable && tomiEndsRaw && tomiEndsRaw !== '__perpetual__' && (
                                  <div className="text-muted small mt-1">
                                    Fuente: {tomiStatus.effective?.source || 'status'}
                                  </div>
                                )}
                              </dd>
                            </dl>
                          </div>
                        )}

                        {canActivateTomi && (
                          <div className="border rounded p-2 mb-2 bg-light">
                            <div className="fw-semibold small mb-2">Activar suscripción TOMi.digital</div>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1">Tipo de producto</Form.Label>
                              <Form.Select
                                size="sm"
                                value={selectedPinTypeId}
                                disabled={pinTypesLoading || savingActivate}
                                onChange={(e) => setSelectedPinTypeId(e.target.value)}
                              >
                                <option value="">
                                  {pinTypesLoading ? 'Cargando tipos…' : 'Selecciona tipo…'}
                                </option>
                                {pinTypes.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {getPinTypeLabel(item)}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1">Motivo (opcional)</Form.Label>
                              <Form.Control
                                size="sm"
                                maxLength={255}
                                value={activateReason}
                                disabled={savingActivate}
                                onChange={(e) => setActivateReason(e.target.value)}
                                placeholder={`Suscripción T8 serial ${subscriber.serial_num}`}
                              />
                            </Form.Group>
                            <p className="text-muted small mb-2">
                              La fecha de fin será el próximo vencimiento T8: <strong>{t8DueYmd || '—'}</strong>
                            </p>
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={savingActivate || !selectedPinTypeId || !t8DueYmd}
                              onClick={handleActivateTomiSubscription}
                            >
                              {savingActivate ? 'Activando…' : 'Activar suscripción'}
                            </Button>
                          </div>
                        )}

                        {canWrite && (
                          <div className="d-flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={savingLink}
                              onClick={openChangeEmailModal}
                            >
                              Cambiar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={savingLink}
                              onClick={handleUnlinkTomiEmail}
                            >
                              Desvincular
                            </Button>
                            {datesOutOfSync && (
                              <Button
                                size="sm"
                                variant="warning"
                                disabled={savingSync}
                                onClick={handleSyncTomiEnds}
                              >
                                {savingSync ? 'Sincronizando…' : 'Sincronizar fechas'}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title">Pagos / Facturas</h5>
                    {(subscriber.payments || []).length === 0 ? (
                      <p className="text-muted mb-0">Sin pagos registrados.</p>
                    ) : (
                      <Table bordered hover size="sm" responsive className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Factura</th>
                            <th>Monto</th>
                            <th>Fecha</th>
                            <th>Notas</th>
                            <th>Registró</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriber.payments.map((pay) => (
                            <tr key={pay.id}>
                              <td>{pay.invoice_number}</td>
                              <td>{formatPrice(pay.amount)}</td>
                              <td>{pay.paid_at}</td>
                              <td>{pay.notes || '—'}</td>
                              <td>{pay.created_by_name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nueva suscripción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Serial</Form.Label>
            <Form.Control
              value={createSerial}
              onChange={(e) => setCreateSerial(e.target.value)}
              placeholder="Serial del dispositivo"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Plan</Form.Label>
            <Form.Select value={createPlanId} onChange={(e) => setCreatePlanId(e.target.value)}>
              <option value="">Selecciona un plan…</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (mensual {formatPrice(p.price_monthly)} / anual {formatPrice(p.price_annual)})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ciclo de cobro</Form.Label>
            <Form.Select value={createCycle} onChange={(e) => setCreateCycle(e.target.value)}>
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
            </Form.Select>
          </Form.Group>
          <hr />
          <p className="text-muted small">Pago inicial (opcional). Sin factura la suscripción queda en mora hasta el primer pago.</p>
          <Form.Group className="mb-3">
            <Form.Label>Nº factura</Form.Label>
            <Form.Control value={createInvoice} onChange={(e) => setCreateInvoice(e.target.value)} />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={createAmount}
                  onChange={(e) => setCreateAmount(e.target.value)}
                  disabled={!createInvoice.trim()}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Fecha de pago</Form.Label>
                <Form.Control
                  type="date"
                  value={createPaidAt}
                  onChange={(e) => setCreatePaidAt(e.target.value)}
                  disabled={!createInvoice.trim()}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={savingCreate || !createSerial.trim() || !createPlanId}
            onClick={handleCreate}
          >
            {savingCreate ? 'Guardando…' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Registrar pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nº factura</Form.Label>
            <Form.Control value={payInvoice} onChange={(e) => setPayInvoice(e.target.value)} />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Fecha de pago</Form.Label>
                <Form.Control
                  type="date"
                  value={payPaidAt}
                  onChange={(e) => setPayPaidAt(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-0">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={savingPayment || !payInvoice.trim() || payAmount === ''}
            onClick={handleAddPayment}
          >
            {savingPayment ? 'Guardando…' : 'Registrar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showChangeEmailModal} onHide={() => setShowChangeEmailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar correo TOMi.digital</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Debe ser una cuenta activa en TOMi.digital
            {T8_MSUBSCRIPTIONS_DEV_SWITCH ? ` (${isQa ? 'QA' : 'PROD'})` : ''}.
            Solo puede haber un correo por suscriptor.
          </p>
          {tomiStatus?.manageable && tomiHasActiveInternal(tomiStatus) && (
            <div className="alert alert-warning py-2 small">
              Esta cuenta tiene una suscripción TOMi.digital que controlamos.
              Al cambiar el correo, esa suscripción se vencerá a <strong>ayer</strong> y quedará inactiva.
            </div>
          )}
          <Form.Group className="mb-0">
            <Form.Label>Nuevo correo</Form.Label>
            <Form.Control
              type="email"
              value={changeEmailInput}
              onChange={(e) => setChangeEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLinkTomiEmail(changeEmailInput);
                }
              }}
              disabled={savingLink}
              placeholder="usuario@ejemplo.com"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChangeEmailModal(false)} disabled={savingLink}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={savingLink || !changeEmailInput.trim()}
            onClick={() => handleLinkTomiEmail(changeEmailInput)}
          >
            {savingLink ? 'Guardando…' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showChangeTypeModal}
        onHide={() => !savingActivate && setShowChangeTypeModal(false)}
        centered
      >
        <Modal.Header closeButton={!savingActivate}>
          <Modal.Title>Cambiar tipo de suscripción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Tipo actual:{' '}
            <strong>
              {tomiStatus?.internal?.plan_type_name
                || tomiStatus?.effective?.plan_type_name
                || '—'}
            </strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de producto</Form.Label>
            <Form.Select
              value={selectedPinTypeId}
              disabled={pinTypesLoading || savingActivate}
              onChange={(e) => setSelectedPinTypeId(e.target.value)}
            >
              <option value="">
                {pinTypesLoading ? 'Cargando tipos…' : 'Selecciona tipo…'}
              </option>
              {pinTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {getPinTypeLabel(item)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Motivo (opcional)</Form.Label>
            <Form.Control
              maxLength={255}
              value={activateReason}
              disabled={savingActivate}
              onChange={(e) => setActivateReason(e.target.value)}
              placeholder={
                subscriber
                  ? `Cambio de tipo T8 serial ${subscriber.serial_num}`
                  : 'Motivo del cambio'
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowChangeTypeModal(false)}
            disabled={savingActivate}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={
              savingActivate
              || !selectedPinTypeId
              || (currentTomiTypeId != null && String(currentTomiTypeId) === String(selectedPinTypeId))
            }
            onClick={handleChangeTomiType}
          >
            {savingActivate ? 'Guardando…' : 'Cambiar tipo'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SuscripcionesT8;
